import { motion } from "framer-motion";

const services = [
  {
    n: "I",
    key: "socialogic",
    division: "Socialogic",
    heading: "We Build Presence",
    body: "Full-service social media management, strategy, content planning, scripting, editing, graphic design, posting, community management, and growth. For founders building personal brands and businesses that want to show up consistently and intentionally across platforms.",
    tags: ["Strategy", "Content", "Community", "Growth"],
    span: "md:col-span-7",
  },
  {
    n: "II",
    key: "studios",
    division: "Butter Honey Studios",
    heading: "We Build Content",
    body: "End-to-end production house. Concept development, shoot planning, filming, editing, and post-production. From brand films and reels to full campaign shoots, everything handled in-house.",
    tags: ["Direction", "Production", "Edit", "Post"],
    span: "md:col-span-5",
  },
  {
    n: "III",
    key: "media",
    division: "Butter Honey Media",
    heading: "We Build IPs",
    body: "Content creation and original IP development. Building content universes, series, formats, and intellectual property for brands that want to own a space in culture, not just occupy a feed.",
    tags: ["Formats", "IP", "Series", "Culture"],
    span: "md:col-span-12",
  },
];

export default function WhatWeDo() {
  return (
    <section
      id="what-we-do"
      data-testid="what-we-do-section"
      className="relative bg-[#0a0705] pb-32 pt-24 md:pb-48 md:pt-32"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="mb-16 flex flex-col items-start justify-between gap-6 md:mb-24 md:flex-row md:items-end">
          <div>
            <p className="overline">
              <span className="text-[#d99036]">✦</span>&nbsp;&nbsp;What We Do
            </p>
            <h2 className="mt-6 max-w-[900px] font-display text-5xl font-black leading-[0.95] tracking-tight md:text-7xl">
              Three divisions.
              <br />
              <span className="font-serif-editorial italic font-normal text-[#d99036]">
                One creative house.
              </span>
            </h2>
          </div>
          <p className="max-w-sm font-body text-sm leading-relaxed text-[#a89f91] md:text-base">
            Each division has its own voice, its own craft, its own obsession. Together, they build brands that endure.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
          {services.map((s, i) => (
            <motion.article
              key={s.key}
              data-testid={`service-card-${s.key}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.9,
                delay: i * 0.08,
                ease: [0.22, 1, 0.36, 1],
              }}
              className={`group relative flex min-h-[360px] flex-col justify-between overflow-hidden border border-[#2a1f17] bg-[#140e0a] p-8 md:min-h-[420px] md:p-10 ${s.span}`}
            >
              {/* corner spotlight */}
              <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#d99036] opacity-0 blur-3xl transition-opacity duration-700 group-hover:opacity-20" />

              <div className="flex items-center justify-between">
                <span className="font-serif-editorial italic text-lg text-[#d99036]">
                  {s.n}.
                </span>
                <span className="overline">{s.division}</span>
              </div>

              <div className="mt-16 md:mt-24">
                <h3 className="font-display text-5xl font-black leading-[0.95] tracking-tight text-[#f9f6f0] md:text-6xl">
                  {s.heading.split(" ").map((word, idx) => (
                    <span
                      key={idx}
                      className={
                        word === "Presence" ||
                        word === "Content" ||
                        word === "IPs"
                          ? "font-serif-editorial italic font-normal text-[#e9c48a]"
                          : ""
                      }
                    >
                      {word}{" "}
                    </span>
                  ))}
                </h3>
                <p className="mt-6 max-w-[540px] font-body text-sm leading-relaxed text-[#a89f91] md:text-base">
                  {s.body}
                </p>
              </div>

              <div className="mt-10 flex flex-wrap gap-2 md:mt-14">
                {s.tags.map((t) => (
                  <span
                    key={t}
                    className="border border-[#2a1f17] px-3 py-1.5 font-body text-[11px] uppercase tracking-[0.18em] text-[#a89f91]"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-20 flex justify-center md:mt-28">
          <a
            href="#contact"
            data-testid="whatwedo-cta"
            className="group inline-flex items-center gap-3 border-b border-[#2a1f17] pb-2 font-display text-sm uppercase tracking-[0.28em] text-[#f9f6f0] transition-colors duration-500 hover:border-[#d99036] hover:text-[#d99036]"
          >
            Book a Discovery Call
            <span
              aria-hidden
              className="inline-block transition-transform duration-500 group-hover:translate-x-2"
            >
              →
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}
